import { useState, useEffect, useRef } from 'react'
import { Box, Flex, Text, VStack, Input, Spinner, Modal, useT } from '@minetrace/ui'
import { LuUpload as Upload, LuFileText as FileIcon, LuX as X } from 'react-icons/lu'
import { useFormColors } from '@minetrace/ui/forms'
import { toaster } from '@minetrace/ui'

interface ScheduleDocUploadModalProps {
  open: boolean
  onClose: () => void
  onUpload: (formData: FormData) => Promise<void>
}

const ScheduleDocUploadModal = ({ open, onClose, onUpload }: ScheduleDocUploadModalProps) => {
  const c = useFormColors()
  const { t } = useT()
  const [saving, setSaving] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [notes, setNotes] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setSelectedFile(null)
      setNotes('')
    }
  }, [open])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size === 0) {
      toaster.error({ description: t('common.emptyFileError') })
      e.target.value = ''
      return
    }
    if (file.type !== 'application/pdf') {
      toaster.error({ description: t('programaciones.wizard.toasts.onlyPdfAllowed') })
      e.target.value = ''
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      toaster.error({ description: t('programaciones.wizard.toasts.fileMaxSize') })
      e.target.value = ''
      return
    }
    setSelectedFile(file)
  }

  const handleSubmit = async () => {
    if (!selectedFile) {
      toaster.error({ description: t('programaciones.wizard.toasts.selectPdfFile') })
      return
    }

    setSaving(true)
    try {
      const formData = new FormData()
      formData.append('notes', notes)
      formData.append('file', selectedFile)

      await onUpload(formData)
      onClose()
      toaster.success({ description: t('programaciones.wizard.toasts.documentUploaded') })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('programaciones.wizard.uploadModal.title')}
      subtitle={t('programaciones.wizard.uploadModal.subtitle')}
      size="md"
      footer={
        <>
          <Box
            as="button" type="button"
            px={5} h="42px" borderRadius="lg" fontSize="sm" fontWeight="600"
            border="1px solid" borderColor="gray.200" color={c.text}
            cursor="pointer" _hover={{ bg: 'gray.50' }}
            display="flex" alignItems="center"
            onClick={onClose}
          >
            {t('programaciones.wizard.uploadModal.cancel')}
          </Box>
          <Box
            as="button" type="button"
            px={5} h="42px" borderRadius="lg" fontSize="sm" fontWeight="600"
            bg="#b4532a" color="white"
            cursor={saving ? 'not-allowed' : 'pointer'}
            opacity={saving ? 0.6 : 1}
            _hover={{ bg: '#085a30' }}
            display="flex" alignItems="center" gap={2}
            onClick={handleSubmit} disabled={saving}
          >
            {saving && <Spinner size="sm" />}
            <Upload size={16} />
            {t('programaciones.wizard.uploadModal.upload')}
          </Box>
        </>
      }
    >
      <VStack gap={4} alignItems="stretch">
        {/* Notas */}
        <Box>
          <Text tid="programaciones.wizard.uploadModal.notesLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
          <Input
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value.slice(0, 500))}
            placeholder={t('programaciones.wizard.uploadModal.notesPlaceholder')}
            maxLength={500}
            bg={c.bgInput}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            color={c.text}
            h="42px"
            fontSize="sm"
            _placeholder={{ color: c.textMuted }}
            _focus={{ outline: 'none', borderColor: '#b4532a', ring: '1px', ringColor: '#b4532a' }}
          />
          <Text fontSize="xs" color={c.textMuted} mt={1} textAlign="right" fontVariantNumeric="tabular-nums">
            {notes.length}/500
          </Text>
        </Box>

        {/* Archivo */}
        <Box>
          <Text tid="programaciones.wizard.uploadModal.fileLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
          <input
            type="file"
            accept=".pdf,application/pdf"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {selectedFile ? (
            <Flex
              p={3} bg="#eef7f6" border="1px solid #dcefed" borderRadius="lg"
              alignItems="center" gap={3} justifyContent="space-between"
            >
              <Flex alignItems="center" gap={2}>
                <FileIcon size={18} color="#b4532a" />
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={c.text}>{selectedFile.name}</Text>
                  <Text fontSize="xs" color={c.textMuted}>{(selectedFile.size / 1024).toFixed(0)} KB</Text>
                </Box>
              </Flex>
              <Box as="button" type="button" p={1} borderRadius="full" color={c.textMuted} cursor="pointer"
                _hover={{ bg: 'gray.100' }} onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }}>
                <X size={16} />
              </Box>
            </Flex>
          ) : (
            <Box
              as="button" type="button"
              w="100%" p={6} textAlign="center"
              border="2px dashed" borderColor="gray.200" borderRadius="lg"
              cursor="pointer" _hover={{ borderColor: '#b4532a', bg: '#eef7f6' }}
              transition="all 0.2s"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={24} color="#b5aa9d" style={{ margin: '0 auto 8px' }} />
              <Text tid="programaciones.wizard.uploadModal.selectFile" fontSize="sm" fontWeight="600" color={c.textMuted} />
              <Text tid="programaciones.wizard.uploadModal.maxSize" fontSize="xs" color="#b5aa9d" />
            </Box>
          )}
        </Box>
      </VStack>
    </Modal>
  )
}

export default ScheduleDocUploadModal
